import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { useCMSBlock } from "@/hooks/useCMSBlock";
import { DEFAULT_HOME_PRICING_BANNER } from "@/lib/cms-defaults";

const comparisonRows: Array<{ feature: string; free: string | boolean; paid: string | boolean }> = [
  { feature: "Sessions", free: "3 per week", paid: "Unlimited" },
  { feature: "Lenses per Session", free: "1", paid: "All 4" },
  { feature: "One clear next step", free: true, paid: true },
  { feature: "Memory across Sessions", free: false, paid: true },
  { feature: "Follow-up questions", free: false, paid: true },
  { feature: "Weekly Reflections", free: false, paid: true },
  { feature: "Dojo (opens Sept 2026)", free: false, paid: "Early access" },
];

function Cell({ value, accent = false }: { value: string | boolean; accent?: boolean }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check
        size={14}
        style={{ color: accent ? "hsl(var(--marigold))" : "hsl(var(--manuscript) / 0.55)" }}
      />
    ) : (
      <X size={14} style={{ color: "hsl(var(--manuscript) / 0.18)" }} />
    );
  }
  return (
    <span
      className={accent ? "font-display italic" : "text-manuscript/65"}
      style={{
        fontSize: "clamp(0.78rem, 1vw, 0.95rem)",
        color: accent ? "hsl(var(--marigold))" : undefined,
        letterSpacing: accent ? "0.01em" : undefined,
      }}
    >
      {value}
    </span>
  );
}

export default function FreePaidScene() {
  const { data: block } = useCMSBlock("home_pricing_banner");
  const cms = block?.content || DEFAULT_HOME_PRICING_BANNER;
  const heading = cms.heading || DEFAULT_HOME_PRICING_BANNER.heading;
  const subheading = cms.subheading || DEFAULT_HOME_PRICING_BANNER.subheading;
  const ctaText = cms.cta_text || DEFAULT_HOME_PRICING_BANNER.cta_text;
  const ctaUrl = cms.cta_url || DEFAULT_HOME_PRICING_BANNER.cta_url;

  return (
    <section
      className="home-band px-5 sm:px-8"
      style={{ background: "hsl(var(--ink))" }}
    >
      <div className="max-w-4xl mx-auto w-full text-left">
        <div
          className="scroll-reveal"
          style={{ marginBottom: "clamp(1.75rem, 4vh, 2.75rem)" }}
        >
          <p className="section-eyebrow mb-3">Free vs Circle</p>
          <h2 className="section-heading">
            {heading.includes(".") ? (
              <>
                {heading.split(".")[0]}.{" "}
                <span className="italic" style={{ color: "hsl(var(--marigold))" }}>
                  {heading.split(".").slice(1).join(".").trim()}
                </span>
              </>
            ) : (
              heading
            )}
          </h2>
        </div>


        <div
          className="border-t"
          style={{ borderColor: "hsl(var(--manuscript) / 0.08)" }}
        >
          <div
            className="grid grid-cols-[1.4fr_1fr_1fr] items-baseline gap-3 sm:gap-6 py-3 border-b"
            style={{ borderColor: "hsl(var(--manuscript) / 0.08)" }}
          >
            <span className="uppercase tracking-[0.3em] text-manuscript/35 text-[10px]">
              Feature
            </span>
            <span className="uppercase tracking-[0.3em] text-manuscript/35 text-[10px] text-center">
              Free
            </span>
            <span
              className="uppercase tracking-[0.3em] text-center"
              style={{ color: "hsl(var(--marigold))", fontSize: "10px" }}
            >
              Circle
            </span>
          </div>

          {comparisonRows.map((row) => (
            <div
              key={row.feature}
              className="scroll-reveal group relative grid grid-cols-[1.4fr_1fr_1fr] items-center gap-3 sm:gap-6 py-3.5 sm:py-4 border-b"
              style={{ borderColor: "hsl(var(--manuscript) / 0.08)" }}
            >
              <span
                aria-hidden
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background:
                    "linear-gradient(90deg, transparent, hsl(var(--manuscript) / 0.025) 30%, hsl(var(--manuscript) / 0.025) 70%, transparent)",
                }}
              />
              <p
                className="relative font-display text-manuscript leading-tight"
                style={{
                  fontSize: "clamp(0.95rem, 1.3vw, 1.1rem)",
                  letterSpacing: "-0.005em",
                }}
              >
                {row.feature}
              </p>
              <div className="relative flex items-center justify-center">
                <Cell value={row.free} />
              </div>
              <div className="relative flex items-center justify-center">
                <Cell value={row.paid} accent />
              </div>
            </div>
          ))}
        </div>

        <div className="scroll-reveal text-left mt-10 sm:mt-12">
          <p
            className="font-display italic text-manuscript/55 mb-6"
            style={{ fontSize: "clamp(0.85rem, 1.05vw, 1rem)" }}
          >
            {subheading}
          </p>
          <Link to={ctaUrl} className="btn-ghost btn-ghost--marigold">
            {ctaText}
          </Link>
        </div>
      </div>
    </section>
  );
}

